import { useContext } from 'react'

import { datadogLogs } from '@datadog/browser-logs'
import { OptimizelyContext } from '@optimizely/react-sdk'
import Cookies from 'js-cookie'

import { getAuthToken } from '@library/auth'
import { canUseWindow } from '@library/environment'

/**
 * Feature flag hook that decides which homepage renders.
 * Uses Optimizely experiment: beetroots_ofx_cwc_homepage
 *
 * The new CwC homepage is the default: this returns `true` in every state
 * (server render, no analytics-cookie consent, SDK not ready, decide error,
 * treatment `t1`, or any non-Control decision) EXCEPT an explicit Control
 * (`c`) decision for an eligible, bucketed visitor, which returns `false`
 * (the legacy homepage). The experiment key and the `t1`/`c` variation names
 * are unchanged, preserving live experiment data continuity.
 *
 * @returns {boolean} true to render the new CwC homepage, false for legacy
 */
export const useIsCwcHomepageEnabled = (): boolean => {
  const { optimizely } = useContext(OptimizelyContext)

  // The CwC homepage is the default. We cannot bucket during server-side
  // rendering, so default to it rather than executing the client-only logic.
  if (!canUseWindow()) {
    return true
  }

  const authToken = getAuthToken()
  const isAuthenticated = !!authToken

  let hasAcceptedAnalytics = false
  try {
    const consentCookieString = Cookies.get('v1_gousto_cookie_consent')
    const consentCookie = consentCookieString ? JSON.parse(consentCookieString) : {}
    hasAcceptedAnalytics = consentCookie?.categories?.includes('ANALYTICS')
  } catch (error) {
    let errorMessage = 'Unknown error when parsing cookie consent'
    if (error instanceof Error) {
      errorMessage = error.message
      datadogLogs.logger.error('Error parsing CwcHomepage cookie consent', {
        error: errorMessage,
        cookieConsent: Cookies.get('v1_gousto_cookie_consent'),
      })
    }
  }

  // We only allocate users to the experiment if they have consented
  // to the Analytics cookies and are not authenticated (unless bypassed)
  let variationKey: string | null | undefined

  if (optimizely && hasAcceptedAnalytics && !isAuthenticated) {
    try {
      const decision = optimizely.decide('beetroots_ofx_cwc_homepage')
      variationKey = decision.variationKey
    } catch (error) {
      datadogLogs.logger.error('Error deciding cwc homepage', {
        error: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }

  // The new CwC homepage is the default for everyone; only an explicit Control
  // (`c`) decision renders the legacy homepage. We key on `variationKey`, not
  // `decision.enabled`: `enabled` is the flag's enabled state — true once a
  // visitor qualifies into the running experiment (audience + traffic allocation)
  // and is bucketed into a variation, and false only for the "everyone else / Off"
  // fallback. It is therefore true for BOTH `c` and `t1`, so it cannot distinguish
  // control from treatment; only `variationKey` can.
  return variationKey !== 'c'
}
